import { useState, useCallback, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  assignAppRole,
  grantAppPermission,
  listAppRoles,
  revokeAppPermission,
  revokeAppRole,
} from '../api/admin.js';
import type { AppPermission, AppRole } from '@m8-app/contracts/auth/roles.js';

const APP_ROLES: AppRole[] = [
  'super_admin',
  'admin',
  'installer',
  'production_test',
  'main_user',
  'sub_main_user',
  'member',
  'sub_member',
];

const PERMISSIONS: AppPermission[] = [
  'members.add',
  'members.remove',
  'devices.configure',
  'devices.transfer',
  'floors.configure',
];

export function AppRoles() {
  const qc = useQueryClient();
  const [mobile, setMobile] = useState('');
  const [role, setRole] = useState<AppRole>('member');
  const [grantUserId, setGrantUserId] = useState('');
  const [permission, setPermission] = useState<AppPermission>('members.add');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyId = useCallback((id: string) => {
    void navigator.clipboard.writeText(id).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    });
  }, []);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['app-roles'],
    queryFn: listAppRoles,
  });

  const assignMutation = useMutation({
    mutationFn: () => assignAppRole({ mobile, role }),
    onSuccess: () => {
      setMsg({ kind: 'ok', text: 'Role assigned' });
      setMobile('');
      qc.invalidateQueries({ queryKey: ['app-roles'] });
    },
    onError: (e: unknown) => setMsg({ kind: 'err', text: (e as { message?: string }).message ?? 'Failed' }),
  });

  const revokeMutation = useMutation({
    mutationFn: (userId: string) => revokeAppRole(userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['app-roles'] }),
  });

  const grantMutation = useMutation({
    mutationFn: () => grantAppPermission({ granteeUserId: grantUserId, permission }),
    onSuccess: () => setMsg({ kind: 'ok', text: 'Permission granted' }),
    onError: (e: unknown) => setMsg({ kind: 'err', text: (e as { message?: string }).message ?? 'Failed' }),
  });

  const revokePermMutation = useMutation({
    mutationFn: () => revokeAppPermission({ granteeUserId: grantUserId, permission }),
    onSuccess: () => setMsg({ kind: 'ok', text: 'Permission revoked' }),
    onError: (e: unknown) => setMsg({ kind: 'err', text: (e as { message?: string }).message ?? 'Failed' }),
  });

  function handleAssign(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    assignMutation.mutate();
  }

  const assignments = data?.assignments ?? [];

  return (
    <div className="p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-1">App Role Hierarchy</h2>
      <p className="text-sm text-gray-500 mb-6">
        Mobile-app roles (CLAUDE.md §3) — assign by mobile number and grant extra permissions to sub-roles.
      </p>

      {msg && (
        <div className={`mb-4 text-sm rounded-lg px-3 py-2 ${
          msg.kind === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {msg.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl">
        {/* Assignments list */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">Current Assignments</h3>
          </div>
          {isLoading ? (
            <p className="px-4 py-4 text-sm text-gray-400">Loading…</p>
          ) : isError ? (
            <p className="px-4 py-4 text-sm text-red-600">
              Couldn't load role assignments. Check your connection and try again.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-left">
                  <tr>
                    <th className="px-4 py-2 font-medium">User ID</th>
                    <th className="px-4 py-2 font-medium">Mobile</th>
                    <th className="px-4 py-2 font-medium">Role</th>
                    <th className="px-4 py-2 font-medium">Assigned</th>
                    <th className="px-4 py-2 font-medium" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {assignments.map((a) => (
                    <tr key={a.userId} className="hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-gray-700 select-all">{a.userId}</span>
                          <button
                            onClick={() => copyId(a.userId)}
                            title="Copy User ID"
                            className="shrink-0 text-xs text-gray-400 hover:text-gray-700"
                          >
                            {copiedId === a.userId ? '✓' : '⎘'}
                          </button>
                          <button
                            onClick={() => setGrantUserId(a.userId)}
                            title="Use in permission form"
                            className="shrink-0 text-xs text-indigo-500 hover:text-indigo-700 whitespace-nowrap"
                          >
                            Use →
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">{a.mobile}</td>
                      <td className="px-4 py-2">
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          {a.role}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-gray-400 text-xs">{a.assignedAt.slice(0, 10)}</td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => revokeMutation.mutate(a.userId)}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          Revoke
                        </button>
                      </td>
                    </tr>
                  ))}
                  {assignments.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">No assignments</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Forms */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Assign Role</h3>
            <form onSubmit={handleAssign} className="space-y-3">
              <div>
                <label htmlFor="ar-mobile" className="block text-sm font-medium text-gray-700 mb-1">Mobile number</label>
                <input
                  id="ar-mobile"
                  type="tel"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  placeholder="+91…"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label htmlFor="ar-role" className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  id="ar-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value as AppRole)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  {APP_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <button
                type="submit"
                disabled={assignMutation.isPending}
                className="w-full bg-indigo-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
              >
                {assignMutation.isPending ? 'Assigning…' : 'Assign'}
              </button>
            </form>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Permission Grants</h3>
            <p className="text-xs text-gray-500 mb-4">Give a sub-role an extra capability beyond its defaults.</p>
            <div className="space-y-3">
              <div>
                <label htmlFor="ar-grant-user" className="block text-sm font-medium text-gray-700 mb-1">
                  User ID
                  <span className="ml-1 text-xs font-normal text-gray-400">(select "Use →" from table)</span>
                </label>
                <div className="relative">
                  <input
                    id="ar-grant-user"
                    type="text"
                    value={grantUserId}
                    onChange={(e) => setGrantUserId(e.target.value)}
                    placeholder="UUID"
                    className={`w-full border rounded-lg px-3 py-2 text-sm font-mono pr-8 ${
                      grantUserId ? 'border-indigo-400 bg-indigo-50' : 'border-gray-300'
                    }`}
                  />
                  {grantUserId && (
                    <button
                      type="button"
                      onClick={() => setGrantUserId('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm leading-none"
                      title="Clear"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label htmlFor="ar-perm" className="block text-sm font-medium text-gray-700 mb-1">Permission</label>
                <select
                  id="ar-perm"
                  value={permission}
                  onChange={(e) => setPermission(e.target.value as AppPermission)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  {PERMISSIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => { setMsg(null); grantMutation.mutate(); }}
                  disabled={!grantUserId || grantMutation.isPending}
                  className="flex-1 bg-green-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  Grant
                </button>
                <button
                  onClick={() => { setMsg(null); revokePermMutation.mutate(); }}
                  disabled={!grantUserId || revokePermMutation.isPending}
                  className="flex-1 bg-gray-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50"
                >
                  Revoke
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
